import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SliderInput from './SliderInput';
import EstimatedSavings from './EstimatedSavings';
import { formatCurrency } from '../utils/phevCalculations';
import { calculateSavings, getLocalizedVehicleName } from '../utils/savingsCalculations';
import cx70Data from '../data/cx70phev.json';
import cx90Data from '../data/cx90phev.json';
import './PhevCalculator.scss';

// The calculator runs in an iframe embedded on mazda.ca, so derive the data
// host from the browser URL at runtime — no recompilation per environment.
// Local development (localhost) falls back to the production host.
const getMazdaHost = (): string => {
  if (typeof window === 'undefined') return 'www.mazda.ca';
  const { hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0') {
    return 'www.mazda.ca';
  }
  return hostname;
};

const DATA_BASE_URL = `https://${getMazdaHost()}/globalassets/mazda-canada/react/phev-calculator/data/`;

const VEHICLE_OPTIONS: Array<{ label: string; url: string; fallback: VehicleData }> = [
  { label: 'Mazda CX-90 PHEV', url: `${DATA_BASE_URL}cx90phev.json`, fallback: cx90Data as VehicleData },
  { label: 'Mazda CX-70 PHEV', url: `${DATA_BASE_URL}cx70phev.json`, fallback: cx70Data as VehicleData },
];

// When the host page sets `window.default_phev_calculator_vehicle` to a valid
// dropdown label, preselect that vehicle and hide the vehicle selector.
// The calculator runs in an iframe, so the host's global lives on the parent
// window; read it there first and fall back to our own window (standalone use).
const getDefaultVehicle = (): unknown => {
  if (typeof window === 'undefined') return undefined;

  const read = (target: Window | undefined): unknown => {
    if (!target) return undefined;
    try {
      return (target as unknown as Record<string, unknown>).default_phev_calculator_vehicle;
    } catch {
      // Cross-origin parent access is not allowed; ignore.
      return undefined;
    }
  };

  return read(window.parent) ?? read(window);
};

const DEFAULT_VEHICLE_CONFIG = (() => {
  const configured = getDefaultVehicle();
  if (typeof configured === 'string' && VEHICLE_OPTIONS.some(o => o.label === configured)) {
    return { label: configured, hideSelector: true };
  }
  return { label: VEHICLE_OPTIONS[0].label, hideSelector: false };
})();

// The host page publishes the visitor's province via
// <meta name="data-region" content="XX">. Same iframe-aware read as above;
// falls back to ON when absent or cross-origin blocked.
const DEFAULT_PROVINCE_CODE = 'ON';

const getHostProvince = (): string => {
  if (typeof window === 'undefined') return DEFAULT_PROVINCE_CODE;

  const read = (target: Window | undefined): string | undefined => {
    if (!target) return undefined;
    try {
      return target.document.querySelector<HTMLMetaElement>('meta[name="data-region"]')?.content?.trim().toUpperCase();
    } catch {
      // Cross-origin parent access is not allowed; ignore.
      return undefined;
    }
  };

  return read(window.parent) ?? read(window) ?? DEFAULT_PROVINCE_CODE;
};

const HOST_PROVINCE = getHostProvince();

// ─── Types ───────────────────────────────────────────────────────────────────

interface VehicleData {
  vehicle: {
    name: string;
    nameFr?: string;
    shortName?: string;
    phevElectricRange: number;
    phevHybridEconomy: number;
    phevElectricEfficiency: number;
  };
  currency: string;
  fuelUnit: string;
  electricityUnit: string;
  distanceUnit: string;
  defaults: {
    weekdayKM: number;
    weekendKM: number;
    iceFuelEconomy: number;
    iceFuelCost: number;
    phevFuelCost: number;
    electricityCost: number;
  };
  sliderRanges: {
    dailyKM: { min: number; max: number; step: number };
    iceFuelEconomy: { min: number; max: number; step: number };
    fuelCost: { min: number; max: number; step: number };
    electricityCost: { min: number; max: number; step: number };
  };
  projectionYears: number;
  translation?: {
    en?: Record<string, string>;
    fr?: Record<string, string>;
  };
  links?: {
    build_yours?: {
      en?: string;
      fr?: string;
    };
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

const PhevCalculator: React.FC = () => {
  const { t, i18n } = useTranslation();

  const [vehicleData, setVehicleData] = useState<VehicleData>(
    VEHICLE_OPTIONS.find(o => o.label === DEFAULT_VEHICLE_CONFIG.label)?.fallback ?? VEHICLE_OPTIONS[0].fallback
  );
  const [selectedVehicle, setSelectedVehicle] = useState(DEFAULT_VEHICLE_CONFIG.label);

  useEffect(() => {
    const option = VEHICLE_OPTIONS.find(o => o.label === selectedVehicle);
    if (!option) return;

    fetch(option.url)
      .then(res => res.json())
      .then(json => setVehicleData(json))
      .catch(() => setVehicleData(option.fallback));
  }, [selectedVehicle]);

  const { vehicle, currency, fuelUnit, electricityUnit, distanceUnit, defaults, sliderRanges, projectionYears, translation, links } = vehicleData;

  const buildYoursUrl = links?.build_yours?.[i18n.language as 'en' | 'fr']?.replace('{prov}', HOST_PROVINCE);

  const [weekdayKM, setWeekdayKM] = useState(defaults.weekdayKM);
  const [weekendKM, setWeekendKM] = useState(defaults.weekendKM);
  const [iceFuelEconomy, setIceFuelEconomy] = useState(defaults.iceFuelEconomy);
  const [phevFuelCost, setPhevFuelCost] = useState(defaults.phevFuelCost);
  const [electricityCost, setElectricityCost] = useState(defaults.electricityCost);

  // ── Calculations ────────────────────────────────────────────────────────────

  const {
    iceYearlyCost,
    yearlySaving,
    monthlySaving,
    projectedSaving,
  } = calculateSavings(
    weekdayKM,
    weekendKM,
    vehicle,
    iceFuelEconomy,
    phevFuelCost,
    electricityCost,
    projectionYears
  );

  // ── Format helpers ──────────────────────────────────────────────────────────

  const fmt = (n: number, decimals = 0) =>
    n.toLocaleString('en-CA', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

  const fmtDecimal = (n: number) => fmt(n, 2);

  return (
    <div className="phev-calculator__container">
      {!DEFAULT_VEHICLE_CONFIG.hideSelector && (
        <div className="phev-calculator__vehicle-selector">
          <h4 id="vehicle-selector-label">{t('selectVehicle')}</h4>
          <select aria-labelledby="vehicle-selector-label" value={selectedVehicle} onChange={e => setSelectedVehicle(e.target.value)}>
            {VEHICLE_OPTIONS.map(opt => (
              <option key={opt.label} value={opt.label}>
                {getLocalizedVehicleName(opt.fallback.vehicle, i18n.language)}
              </option>
            ))}
          </select>
        </div>
      )}
    <div className="phev-calculator">
      <EstimatedSavings
        className="mobile-only"
        currency={currency}
        projectedSaving={projectedSaving}
        projectionYears={projectionYears}
        yearlySaving={yearlySaving}
        monthlySaving={monthlySaving}
        weekdayKM={weekdayKM}
        weekendKM={weekendKM}
        vehicle={vehicle}
        translation={translation}
        phevFuelCost={phevFuelCost}
        electricityCost={electricityCost}
        distanceUnit={distanceUnit}
        iceYearlyCost={iceYearlyCost}
        buildYoursUrl={buildYoursUrl}
      />

      {/* ── Main panel ─────────────────────────────────────────────────────── */}
      <main className="phev-calculator__main">
        <div className="phev-calculator__main-content">
          {/* Distance Per Day */}
          <section className="phev-calculator__section">
            <div>
              <span className="phev-calculator__step-label">{t('STEP_1')}</span>
              <h5 className="phev-calculator__section-title">{t('distancePerDay')}</h5>
            </div>
            <div className="phev-calculator__row">
              <SliderInput
                label={t('weekday')}
                trackingId="weekday_km"
                value={weekdayKM}
                min={sliderRanges.dailyKM.min}
                max={sliderRanges.dailyKM.max}
                step={sliderRanges.dailyKM.step}
                unit={`${distanceUnit} /${t('day')}`}
                onChange={setWeekdayKM}
              />
              <SliderInput
                label={t('weekendDay')}
                trackingId="weekend_km"
                value={weekendKM}
                min={sliderRanges.dailyKM.min}
                max={sliderRanges.dailyKM.max}
                step={sliderRanges.dailyKM.step}
                unit={`${distanceUnit} /${t('day')}`}
                onChange={setWeekendKM}
              />
            </div>
          </section>

          {/* Current Vehicle */}
          <section className="phev-calculator__section">
            <div>
              <span className="phev-calculator__step-label">{t('STEP_2')}</span>
              <h5 className="phev-calculator__section-title">{t('yourCurrentVehicle')}</h5>
            </div>
            <div className="phev-calculator__row">
              <SliderInput
                label={t('fuelEconomy')}
                trackingId="ice_fuel_economy"
                value={iceFuelEconomy}
                min={sliderRanges.iceFuelEconomy.min}
                max={sliderRanges.iceFuelEconomy.max}
                step={sliderRanges.iceFuelEconomy.step}
                unit={`${fuelUnit}/ 100 ${distanceUnit}`}
                formatValue={fmtDecimal}
                onChange={setIceFuelEconomy}
                wide={true}
              />
            </div>
          </section>

          {/* PHEV Vehicle */}
          <section className="phev-calculator__section">
            <div>
            <span className="phev-calculator__step-label">{t('STEP_3')}</span>
            <h5 className="phev-calculator__section-title">{t('yourEnergyCosts')}</h5>
            </div>
            <div className="phev-calculator__row">
              <SliderInput
                label={t('gas')}
                trackingId="phev_fuel_cost"
                value={phevFuelCost}
                min={sliderRanges.fuelCost.min}
                max={sliderRanges.fuelCost.max}
                step={sliderRanges.fuelCost.step}
                unit={` / ${fuelUnit}`}
                formatValue={(v) => formatCurrency(v, currency, i18n.language)}
                onChange={setPhevFuelCost}
              />
              <SliderInput
                label={t('electricity')}
                trackingId="electricity_cost"
                value={electricityCost}
                min={sliderRanges.electricityCost.min}
                max={sliderRanges.electricityCost.max}
                step={sliderRanges.electricityCost.step}
                unit={` / ${electricityUnit}`}
                formatValue={(v) => formatCurrency(v, currency, i18n.language)}
                onChange={setElectricityCost}
              />
            </div>
          </section>
        </div>
      </main>

      <EstimatedSavings
        className="desktop-only"
        currency={currency}
        projectedSaving={projectedSaving}
        projectionYears={projectionYears}
        yearlySaving={yearlySaving}
        monthlySaving={monthlySaving}
        weekdayKM={weekdayKM}
        weekendKM={weekendKM}
        vehicle={vehicle}
        translation={translation}
        phevFuelCost={phevFuelCost}
        electricityCost={electricityCost}
        distanceUnit={distanceUnit}
        iceYearlyCost={iceYearlyCost}
        buildYoursUrl={buildYoursUrl}
      />
    </div>

    <p className="phev-calculator__disclaimer">
      {t('disclaimer', { vehicleName: getLocalizedVehicleName(vehicle, i18n.language) })}
    </p>

    </div>
  );
};

export default PhevCalculator;
